import { BooleanField, FunctionField, NumberField, Show, SimpleShowLayout, TextField, useNotify } from 'react-admin';
import React from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import tz from 'dayjs/plugin/timezone';
import { createDateTimeRenderer } from '../../utils/misc';

dayjs.extend(utc);
dayjs.extend(tz);

/**
 * Renders the evidence block: what each lookup returned, or that it was never
 * made.
 *
 * The ladder is first-match-wins, so a block being absent is information, not a
 * gap — it means the verdict was reached before that source was consulted.
 * Printing the raw object keeps that distinction visible; flattening it into
 * fields would turn "never looked" and "looked, found nothing" into the same
 * blank cell, which is the confusion SPEED-3701 set out to remove.
 */
const renderEvidence = (record: any) =>
  record?.evidence ? (
    <pre style={{ margin: 0, fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
      {JSON.stringify(record.evidence, null, 2)}
    </pre>
  ) : null;

/**
 * The most specific detail the failure carries.
 *
 * `fetchApi` parses the body as JSON when the response says so and falls back
 * to `response.text()` otherwise, so `error` is either the parsed object or the
 * raw string — and the string case is the one where the generic fallbacks say
 * least, so it must be read first.
 */
export const describeLookupFailure = (e: any): string => {
  const detail = typeof e?.error === 'string' ? e.error : e?.error?.errorMessage ?? e?.message ?? '';
  return `Banner lookup failed: ${e?.status ?? ''} ${detail}`.trim();
};

export const BannerDetails = () => {
  const notify = useNotify();

  // react-admin's default onError reports every failure as
  // "Element does not exist" and redirects to a list route this resource does
  // not have. For a page whose whole purpose is telling support WHY a verdict
  // came out the way it did, collapsing a 401, a 400 and a Speedboat outage
  // into "doesn't exist" reproduces the guessing SPEED-3701 set out to end.
  const onError = (e: any) => notify(describeLookupFailure(e), { type: 'error' });

  return (
    <Show queryOptions={{ onError }}>
      <SimpleShowLayout>
        <BooleanField source="isVisible" label="Banner visible" />
        <TextField source="reason" label="Reason" />
        <TextField source="reasonDetail" label="Reason detail" emptyText="—" />
        <TextField source="matchedSource" label="Matched source" />
        <FunctionField source="evaluatedAt" label="Evaluated at" render={createDateTimeRenderer('evaluatedAt')} />
        <BooleanField source="isDelayed" />
        <NumberField source="daysToBannerAvailable" />
        <NumberField source="creditLimit" label="Credit limit (£)" />
        <FunctionField source="evidence" label="Evidence" render={renderEvidence} />
      </SimpleShowLayout>
    </Show>
  );
};
